/// <amd-module name="Test/TestResultWorkItems/Components/TestResultWorkItems" />
import * as React from "react";
import { IViewContextData } from "Test/common-util/Common";
import { TestCaseResult } from "Test/common-util/Contracts/Test";
import { ITestResultDetails } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import { WorkItemsActionsCreator } from "Test/TestResultWorkItems/Actions/WorkItemsActionCreator";
import { WorkItemsActionsHub } from "Test/TestResultWorkItems/Actions/WorkItemsActionHub";
import { BugsGridView } from "Test/TestResultWorkItems/Components/BugsGridView";
import { RequirementsGridView } from "Test/TestResultWorkItems/Components/RequirementsGridView";
import * as Resources from "Test/TestResultWorkItems/Resources";
import { WorkItemsGridViewSource } from "Test/TestResultWorkItems/Source/WorkItemsGridViewSource";
import { IWorkItemsState, WorkItemsViewStore } from "Test/TestResultWorkItems/Store/WorkItemsViewStore";
import * as TelemetryConstants from "Test/TestResultWorkItems/TelemetryConstants";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";

export interface IWorkItemsViewProps extends IVssComponentProperties {
    viewContext: IViewContextData;
    testResults: ITestResultDetails;
    testCaseResult: TestCaseResult;
}

export class TestResultsWorkItems extends VssComponent<IWorkItemsViewProps, IWorkItemsState> {
    private _pageContext: IVssComponentContext;
    private _store: WorkItemsViewStore;
    private _actionsHub: WorkItemsActionsHub;
    private _actionCreator: WorkItemsActionsCreator;
    private _source: WorkItemsGridViewSource;
    constructor(props: IWorkItemsViewProps, pageContext: IVssComponentContext) {
        super(props);
        this._pageContext = pageContext;
        this._actionsHub = new WorkItemsActionsHub();
        this._store = new WorkItemsViewStore(this._pageContext.pageContext, this._actionsHub);
        this._source = new WorkItemsGridViewSource();
        this._actionCreator = new WorkItemsActionsCreator(this._pageContext.pageContext, this.props.viewContext, this._actionsHub, this._source);
        Telemetry.publishEvent(this._pageContext.pageContext, this.props.viewContext, TelemetryConstants.featureWorkItemsPivotGridView_WorkItemPivotClicked, {
            "RunId": this.props.testCaseResult.testRun.id,
            "ResultId": this.props.testCaseResult.id,
            "TestResultOutcome": this.props.testCaseResult.outcome
        });
    }

    public componentWillMount(): void {
        this._actionCreator.clearState();
        this._handleStoreChange();
        this._store.addChangedListener(this._handleStoreChange);
    }

    public componentDidMount(): void {
        this._actionCreator.loadAssociatedWorkItems(this.props.testCaseResult);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._handleStoreChange);
        this._store.dispose();
    }

    public render(): JSX.Element {
        let buttonProps: IButtonProps[] = [{ ariaLabel: Resources.ClearErrorMessage }];
        return (
            <div className="work-items-container-component">
                {
                    this.state.errorMessage ?
                        <Tooltip text={this.state.errorMessage}>
                            <span>
                                <MessageBar
                                    severity={MessageBarSeverity.Error}
                                    buttonProps={buttonProps}
                                    className="preview-attachment-error-bar">
                                    {this.state.errorMessage}
                                </MessageBar>
                            </span>
                        </Tooltip>
                        :
                        ""
                }
                {
                    this.state.isLoading &&
                    this._showLoading()
                }
                {
                    this.state.bugsWorkItems && this.state.bugsWorkItems.length > 0 &&
                    <BugsGridView
                        viewContext={this.props.viewContext}
                        testCaseResult={this.props.testResults && this.props.testCaseResult}
                        actionHub={this._actionsHub}
                        actionCreator={this._actionCreator}
                        workItems={this.state.bugsWorkItems}
                        hasPublishTestResultsPermission={this.state.hasPublishTestResultsPermission}
                    />
                }
                {
                    this.state.requirementsWorkItems && this.state.requirementsWorkItems.length > 0 &&
                    <RequirementsGridView
                        viewContext={this.props.viewContext}
                        testCaseResult={this.props.testResults && this.props.testCaseResult}
                        actionHub={this._actionsHub}
                        actionCreator={this._actionCreator}
                        workItems={this.state.requirementsWorkItems}
                        hasPublishTestResultsPermission={this.state.hasPublishTestResultsPermission}
                    />
                }
                {
                    !this.state.isLoading && this.state.bugsWorkItems.length === 0 && this.state.requirementsWorkItems.length === 0 &&
                    this._showNoWorkItemsImage()
                }
            </div>
        );
    }

    private _showNoWorkItemsImage(): JSX.Element {
        return (
            <div className="no-work-items-area flex-column">
                <div className="no-work-items-icon">
                    <Icon
                        iconName="WorkItem"
                        className="no-work-items-icon"
                    />
                </div>
                <div className="no-work-items-message">
                    {Resources.NoWorkItemsMessage}
                </div>
            </div>
        );
    }

    private _showLoading(): JSX.Element {
        return (
            <div className="work-items-pane-loading flex-column">
                <Spinner
                    ariaLabel={Resources.LoadingWorkItems}
                    className="work-items-grid-view-loading-spinner test-result-loading-spinner-separator"
                    size={SpinnerSize.large}
                    label={Resources.LoadingWorkItems}
                />
            </div>
        );
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    }
}

Components.add("test-resultWorkItems", TestResultsWorkItems);
